import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  inject
} from '@angular/core';
import { AgGridService, BlotterRow } from '@approot/services';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';
import {
  AgGridEvent,
  ColumnApi,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  SideBarDef
} from 'ag-grid-community';
import { clone } from 'lodash';
import { applyColumnState } from './pnl-pivot.funcs';
import { PNLPivotService } from './pnl-pivot.service';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

@Component({
  selector: 'app-pnl-pivot-table',
  templateUrl: './pnl-pivot-table.component.html',
  styleUrls: ['./pnl-pivot-table.component.scss']
})
export class PnlPivotTableComponent implements OnInit {
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  @Input() public headers: { field: string }[] = [];
  @Input() public rowData: Record<string, number>[] = [];
  @Input() public timestamp: number;
  @Input() public index: number;
  @Input() public columnState: ColumnState[];
  public gridOptions: GridOptions;
  public dragging = false;
  public windowTop = 39;
  public windowLeft = 950;
  public sideBarDef: SideBarDef;
  public gridApi: GridApi<BlotterRow>;
  public columnApi: ColumnApi;
  private agGridService = inject(AgGridService);
  private renderer = inject(Renderer2);
  public pnlPivotService = inject(PNLPivotService);
  private winIdx = inject(WindowIndexService);
  private elementRef = inject(ElementRef);

  public ngOnInit(): void {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.gridOptions = this.initializeGridOptions();
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    if (this.columnState) {
      applyColumnState(params.columnApi, clone(this.columnState));
    }
    this.gridApi.setPinnedBottomRowData(this.getGrandTotalRow());
  }

  public closeGrid(): void {
    this.pnlPivotService.removePnlPivotGrid(this.index);
  }

  public dragStart(): void {
    this.dragging = true;
  }

  public async dragEnd(): Promise<void> {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.pnl-pivot-table-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public updateZIndex(): void {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.pnl-pivot-table-drag'),
      this.renderer
    );
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    this.gridApi.setPinnedBottomRowData(this.getGrandTotalRow());
    this.pnlPivotService.savePnlPivotGridState(this.index, allState);
  }

  public clearFilters(): void {
    this.gridApi.setFilterModel({});
    this.gridApi.onFilterChanged();
    this.columnApi.resetColumnState();
  }

  private initializeGridOptions(): GridOptions {
    return {
      groupIncludeFooter: true,
      groupIncludeTotalFooter: true,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true
      },
      autoGroupColumnDef: {
        headerName: 'Overview',
        cellRendererParams: {
          innerRenderer: this.grandTotalCellRenderer.bind(this)
        }
      }
    };
  }

  private grandTotalCellRenderer(params: ICellRendererParams): string {
    return params.node.rowPinned === 'bottom'
      ? 'Grand Total'
      : params.value?.toString() || '';
  }

  public getGrandTotalRow(): Record<string, number>[] {
    if (!this.rowData.length) return [];

    const excludedFields = ['spotStart', 'spotEnd', 'spotMoveInPercent'];

    const grandTotalRow = this.headers.reduce(
      (totals, header) => {
        if (header.field && !excludedFields.includes(header.field)) {
          const total = this.rowData.reduce(
            (sum, row) => sum + (row[header.field] || 0),
            0
          );
          totals[header.field] = total;
        }
        return totals;
      },
      {} as Record<string, number>
    );

    return [grandTotalRow];
  }
}
